import { ReactNode, useEffect, useId, useState } from 'react';
import { Activity, Calendar, File as FileIcon, Grid3x3, Hash, Heading, ImageIcon, List, ListOrdered, Table2, Tags, ToggleLeft, Type, X, Plus } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { INPUT_ID_PATTERN, InputKind } from '@/reports/inputs';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';

export const KIND_ICONS: Record<InputKind | 'section', typeof Hash> = {
  number: Hash, text: Type, choice: List, yesno: ToggleLeft, date: Calendar, names: Tags, numbers: ListOrdered,
  waveform: Activity, image: ImageIcon, file: FileIcon, table: Table2, sweep: Grid3x3, section: Heading,
};

/** The palette: what can be added to a sheet, in the groups the designer shows them. */
export const PALETTE: Array<{ group: string; kinds: Array<InputKind | 'section'> }> = [
  { group: 'rv2PaletteValues', kinds: ['number', 'text', 'choice', 'yesno', 'date'] },
  { group: 'rv2PaletteLists', kinds: ['names', 'numbers'] },
  { group: 'rv2PaletteCaptures', kinds: ['waveform', 'image', 'file'] },
  { group: 'rv2PaletteGroups', kinds: ['table', 'sweep', 'section'] },
];

/**
 * The drag payloads the canvas accepts: a palette tile, or an input being moved,
 * and the same for a section, which drops between sections rather than in one.
 * Only a payload's type can be read while dragging, so each has its own.
 */
export const DRAG_NEW = 'application/x-veriform-new-input';
export const DRAG_MOVE = 'application/x-veriform-move-input';
export const DRAG_NEW_SECTION = 'application/x-veriform-new-section';
export const DRAG_MOVE_SECTION = 'application/x-veriform-move-section';

export const kindLabel = (kind: InputKind | 'section') => I18n.get(`rv2Tile_${kind}`);

/** An on/off switch with its label and a line of help. */
export function Switch({ label, help, checked, onChange }: { label: string; help?: string; checked: boolean; onChange: (checked: boolean) => void }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-[0.75rem]">
      <div className="flex flex-col">
        <label htmlFor={id} className="text-sm font-medium">{label}</label>
        {help && <span className="text-xs text-muted-foreground">{help}</span>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn('relative mt-0.5 h-[18px] w-8 shrink-0 rounded-full transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50', checked ? 'bg-sky-700' : 'bg-input')}
      >
        <span className={cn('absolute top-[2px] size-[14px] rounded-full bg-white shadow-sm transition-[left]', checked ? 'left-[16px]' : 'left-[2px]')} />
      </button>
    </div>
  );
}

/** A labelled property in the panel. */
export function Prop({ label, htmlFor, children, hint }: { label: string; htmlFor?: string; children: ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">{label}</label>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

/**
 * An identifier edited in place: only committed once it is one, and not taken.
 * `compact` drops the heading and help for a row with no room, such as a
 * sweep's condition, and names the field by `label` instead.
 */
export function IdField({ value, taken, onCommit, compact, label = I18n.get('rv2Identifier'), takenMessage = I18n.get('rv2IdTaken') }: {
  value: string;
  taken: (id: string) => boolean;
  onCommit: (id: string) => void;
  compact?: boolean;
  label?: string;
  takenMessage?: string;
}) {
  const id = useId();
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const problem = text === value ? null : !INPUT_ID_PATTERN.test(text) ? I18n.get('rv2IdInvalid') : taken(text) ? takenMessage : null;
  const field = (
    <Input id={id} aria-label={compact ? label : undefined} className={cn('font-mono text-xs', compact && 'h-6 px-1.5')} value={text} aria-invalid={!!problem || undefined}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { if (!problem && text !== value) onCommit(text); else setText(value); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
  if (!compact) return <Prop label={label} htmlFor={id} hint={problem ?? I18n.get('rv2IdentifierHelp')}>{field}</Prop>;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      {field}
      {problem && <span className="text-xs text-destructive">{problem}</span>}
    </div>
  );
}

/** A list of short strings edited in place: choices, column titles. */
export function StringListEditor({ values, onChange, addLabel, itemLabel }: { values: string[]; onChange: (values: string[]) => void; addLabel: string; itemLabel: (index: number) => string }) {
  return (
    <div className="flex flex-col gap-1">
      {values.map((v, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input aria-label={itemLabel(i)} value={v} onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))} />
          <button type="button" aria-label={I18n.get('rv2RemoveItem', { item: v || itemLabel(i) })} className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => onChange(values.filter((_, j) => j !== i))}>
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" className="self-start" onClick={() => onChange([...values, ''])}><Plus /> {addLabel}</Button>
    </div>
  );
}
